"use client";
import {LexicalComposer } from '@lexical/react/LexicalComposer';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {ListPlugin} from '@lexical/react/LexicalListPlugin';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {OnChangePlugin} from "@lexical/react/LexicalOnChangePlugin";
import {EditorState, $getRoot} from "lexical";
import {$generateNodesFromDOM} from '@lexical/html';
import {useLayoutEffect, useState, useRef} from "react";
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import ShadowDomWrapper from "./ShadowDomWrapper.tsx";
import theme from "./theme";
import nodes from "./nodes";
import FloatingTextFormatToolbarPlugin from "./plugins/FloatingTextFormatToolbarPlugin.tsx";
import {ToolbarContext} from "./context/ToolbarContext.tsx";
import {useActiveEditor} from "./context/ActiveEditorContext.tsx";
import LinkPlugin from "./plugins/LinkPlugin";
import FloatingLinkEditorPlugin from "./plugins/FloatingLinkEditorPlugin";
import ShortcutsPlugin from "./plugins/ShortcutsPlugin";

function ShortcutsPluginWrapper({setIsLinkEditMode}: {setIsLinkEditMode: React.Dispatch<React.SetStateAction<boolean>>}) {
    const [editor] = useLexicalComposerContext();
    return <ShortcutsPlugin editor={editor} setIsLinkEditMode={setIsLinkEditMode} />;
}

function onError(error: any) {
    console.error(error);
}

interface EditorProps {
    value: string|object;
    onChange: (value: string|object) => void;
    useShadowWrapper?: boolean | undefined;
    placeholder?: string|null;
}

function MountedPlugin({value}: {value: any}) {
    const [editor] = useLexicalComposerContext();
    const [isInitialized, setInitialized] = useState<boolean>(false);

    useLayoutEffect(() => {
        if (editor && value && !isInitialized) {
            setInitialized(true);
            editor.update(() => {
              if (typeof value === 'string') {
                // remove empty pharagraph from editor start
                $getRoot().clear();
                $getRoot().select();
                const parser = new DOMParser();
                const dom = parser.parseFromString(value, 'text/html');
                const nodes = $generateNodesFromDOM(editor, dom);
                const selection = $getRoot().select();
                selection.insertNodes(nodes);
              } else {
                editor.setEditorState(editor.parseEditorState(value));
              }
            })
        }
    }, [editor, isInitialized, value]);

    return null
}

let uniqueId = 0;
export default function Editor({ value, onChange, useShadowWrapper, placeholder }: EditorProps) {
    const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);
    const namespace = useRef('MyEditor' + (uniqueId++)).current;
    const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
    const editorWrapperRef = useRef<HTMLDivElement | null>(null);
    const onRef = (_floatingAnchorElem: HTMLDivElement) => {
      if (_floatingAnchorElem !== null) {
        setFloatingAnchorElem(_floatingAnchorElem);
      }
    };

    const {setActiveEditor} = useActiveEditor();

    const initialConfig = {
        namespace: namespace,
        theme,
        nodes,
        onError,
    };

    const contentEditable = <ContentEditable
        style={{position: 'relative'}}
        onFocus={() => setActiveEditor(initialConfig.namespace)}
        aria-placeholder={placeholder || 'Enter some text...'}
        placeholder={<div style={{
          opacity: '0.6',
          position: 'absolute',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          userSelect: 'none',
          display: 'inherit',
          width: '100%',
          pointerEvents: 'none',
          top: 0,
          whiteSpace: 'nowrap',
        }}>{placeholder || 'Enter some text...'}</div>}
    />;
    const shadowWrapper = <ShadowDomWrapper ref={editorWrapperRef} useTailwindStyles={false}>
        {contentEditable}
    </ShadowDomWrapper>;
    return (
        <LexicalComposer initialConfig={initialConfig}>
            <ToolbarContext namespace={initialConfig.namespace}>
              {floatingAnchorElem && (
                <>
                  <FloatingTextFormatToolbarPlugin
                    anchorElem={floatingAnchorElem}
                    setIsLinkEditMode={setIsLinkEditMode}
                  />
                  <FloatingLinkEditorPlugin
                    anchorElem={floatingAnchorElem}
                    shadowRootParent={editorWrapperRef.current}
                    isLinkEditMode={isLinkEditMode}
                    setIsLinkEditMode={setIsLinkEditMode}
                  />
                </>
              )}
              <LinkPlugin hasLinkAttributes={true}/>
              <RichTextPlugin
                  contentEditable={
                      <div className="editor-shell" style={{position: 'relative'}} ref={onRef}>
                          {useShadowWrapper ? shadowWrapper : contentEditable}
                      </div>
                  }
                  ErrorBoundary={LexicalErrorBoundary}
              />
              <ListPlugin />
              <HistoryPlugin />
              <ShortcutsPluginWrapper setIsLinkEditMode={setIsLinkEditMode} />
              <MountedPlugin value={value} />
              <OnChangePlugin onChange={(editorState: EditorState) => {
                  onChange(editorState.toJSON());
              }} />
            </ToolbarContext>
        </LexicalComposer>
    );
}